import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense } from "react";

import { useReveal } from "@/hooks/use-reveal";
import { metaDesc } from "@/lib/seo-description";
import { trackEvent } from "@/lib/analytics";
import { LOCAL_BUSINESS_SCHEMA, SITE_URL } from "@/lib/schema-org";

import heroImg from "@/assets/dj-mariage-dance.jpg";
import receptionImg from "@/assets/dj-mariage-reception.jpg";
import equipmentImg from "@/assets/dj-mariage-equipment.jpg";

const GoogleReviewsCarousel = lazy(() =>
  import("@/components/google-reviews-carousel").then((m) => ({ default: m.GoogleReviewsCarousel })),
);

const CANON = "https://dj-val.fr/dj-avignon";
const TITLE = "DJ Avignon – Mariage, soirée & événement | DJ Val";
const DESC =
  "DJ à Avignon pour mariage, soirée privée et entreprise : 20 ans d'expérience, sono et lumière incluses, un seul événement par week-end. Devis en 24 h, note Google 5,0.";

const PRESTATIONS = [
  {
    t: "DJ mariage Avignon",
    d: "Cérémonie sonorisée, vin d'honneur, ouverture de bal et dancefloor jusqu'au bout de la nuit. Plus de 200 mariages animés, un seul événement par week-end.",
    to: "/dj-mariage/$ville" as const,
    params: { ville: "avignon" },
  },
  {
    t: "Anniversaire & soirée privée",
    d: "30, 40, 50, 60 ans, surprise party ou soirée entre amis : une animation qui remplit la piste sans jamais couvrir les conversations du repas.",
    to: "/dj-anniversaire/$ville" as const,
    params: { ville: "avignon" },
  },
  {
    t: "Soirée d'entreprise",
    d: "Gala, séminaire, arbre de Noël, soirée de comité d'entreprise : DJ déclaré SACEM, assuré Hiscox, SIRET et facture conforme.",
    to: "/dj-entreprise/$ville" as const,
    params: { ville: "avignon" },
  },
];

const AUTOUR = [
  "Villeneuve-lès-Avignon",
  "Le Pontet",
  "Les Angles",
  "Châteaurenard",
  "Cavaillon",
  "L'Isle-sur-la-Sorgue",
  "Carpentras",
  "Orange",
];

const FAQ = [
  {
    q: "Quel est le prix d'un DJ à Avignon ?",
    a: "Une soirée privée ou un anniversaire à Avignon se situe généralement entre 690 € et 990 € selon la durée. Les formules mariage démarrent à 1 490 €, sono, éclairage, micros HF, installation et démontage compris. Le devis détaillé vous est transmis en 24 h.",
  },
  {
    q: "Combien de temps à l'avance faut-il réserver un DJ à Avignon ?",
    a: "Comptez 9 à 12 mois pour un samedi de mariage entre mai et septembre. Pour un anniversaire, une soirée privée ou un événement d'entreprise, 4 à 8 semaines suffisent le plus souvent. Je n'accepte qu'un seul événement par week-end : la date se bloque à la signature.",
  },
  {
    q: "Le déplacement sur Avignon est-il inclus dans le tarif ?",
    a: "Oui. Je suis basé à Cavaillon, à vingt-cinq minutes d'Avignon : le déplacement sur la commune et sa périphérie immédiate est compris dans le prix annoncé, sans supplément kilométrique ni frais de dossier.",
  },
  {
    q: "Le matériel son et lumière est-il fourni ?",
    a: "Oui, intégralement. Régie Pioneer, système de diffusion RCF dimensionné à votre salle, éclairage LED de piste, deux micros HF pour les discours et un matériel de secours sur place. Vous n'avez rien à louer par ailleurs.",
  },
  {
    q: "Jusqu'à quelle heure peut durer une soirée dansante à Avignon ?",
    a: "Cela dépend de la salle et de son règlement : en centre-ville d'Avignon, la coupure se situe souvent entre 1 h et 2 h, alors que les domaines de la périphérie autorisent fréquemment jusqu'à 3 h ou 4 h. Le point est vérifié avec le lieu avant la signature.",
  },
  {
    q: "Peut-on ajouter un photobooth à la prestation DJ ?",
    a: "Oui, le photobooth s'ajoute à toute prestation DJ à Avignon : borne installée, impressions illimitées et accessoires. Il est aussi disponible en location seule.",
  },
];

export const Route = createFileRoute("/dj-avignon")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: metaDesc(DESC) },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: metaDesc(DESC) },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANON },
      { property: "og:image", content: `${SITE_URL}/og-dj-val.jpg` },
    ],
    links: [{ rel: "canonical", href: CANON }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "DJ Avignon",
          description: DESC,
          serviceType: "DJ Avignon",
          provider: { ...LOCAL_BUSINESS_SCHEMA },
          areaServed: [
            { "@type": "City", name: "Avignon" },
            { "@type": "City", name: "Cavaillon" },
            { "@type": "AdministrativeArea", name: "Vaucluse" },
          ],
          url: CANON,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Accueil", item: "https://dj-val.fr/" },
            { "@type": "ListItem", position: 2, name: "DJ Avignon", item: CANON },
          ],
        }),
      },
    ],
  }),
  component: DjAvignonPage,
});

function DjAvignonPage() {
  useReveal();

  return (
    <div className="bg-background text-muted-foreground">
      {/* HERO */}
      <section className="on-dark relative min-h-[80vh] flex items-center px-6 overflow-hidden pt-24">
        <div className="absolute inset-0 z-0">
          <img
            src={heroImg}
            alt="Piste de danse animée par DJ Val lors d'une soirée à Avignon"
            className="h-full w-full object-cover opacity-40 kenburns-img"
            fetchPriority="high"
            decoding="async"
            width={1920}
            height={1080}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to right, var(--ink) 0%, color-mix(in oklab, var(--ink) 70%, transparent) 55%, color-mix(in oklab, var(--ink) 30%, transparent) 100%), linear-gradient(to bottom, var(--ink) 0%, transparent 40%, var(--ink) 100%)",
            }}
          />
        </div>
        <div className="relative z-10 max-w-3xl reveal space-y-6">
          <p className="eyebrow">DJ Avignon · Vaucluse</p>
          <h1 className="font-serif-display text-foreground text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            <span className="italic font-light">DJ à Avignon :</span>
            <span className="block font-light gold-text-shine mt-2">mariage, soirée privée, entreprise</span>
          </h1>
          <span className="gold-rule !w-12" />
          <p className="text-base text-foreground/85 font-light leading-relaxed max-w-xl">
            Stéphane, alias DJ Val, DJ professionnel installé à Cavaillon depuis 20 ans. Sono, éclairage et micros
            inclus, un seul événement par week-end, devis détaillé en 24 h.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/contactez-nous"
              onClick={() => trackEvent("cta_click", { cta_label: "Devis gratuit", cta_location: "dj_avignon_hero" })}
              className="bg-[var(--gold)] text-[var(--ink)] px-8 py-3.5 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all"
            >
              Devis gratuit 24 h
            </Link>
            <a
              href="tel:0767908667"
              onClick={() => trackEvent("contact_click", { contact_method: "tel", cta_location: "dj_avignon" })}
              className="border border-[var(--gold-line)] text-foreground px-8 py-3.5 text-[11px] font-semibold uppercase tracking-[0.25em] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors"
            >
              07 67 90 86 67
            </a>
          </div>
          <div className="flex flex-wrap gap-6 pt-6 text-xs uppercase tracking-[0.2em] text-foreground/85">
            <span>★ 5,0 / 5 Google · 40+ avis</span>
            <span>· 200+ mariages</span>
            <span>· 20 ans d'expérience</span>
          </div>
        </div>
      </section>

      {/* RÉPONSE DIRECTE */}
      <section className="py-20 px-6 border-b border-[var(--gold-line)]">
        <div className="max-w-3xl mx-auto sr" data-reveal>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground">
            Un DJ professionnel à Avignon depuis 20 ans
          </h2>
          <span className="gold-rule !w-12 my-8" />
          <p className="text-[15px] text-foreground/90 font-light leading-[1.9]">
            DJ Val est un DJ à Avignon basé à Cavaillon, à vingt-cinq minutes du centre-ville, qui anime mariages,
            anniversaires, soirées privées et événements d'entreprise dans tout le Vaucluse depuis vingt ans. La
            prestation comprend la sonorisation, l'éclairage, les micros et l'installation complète, pour un devis
            transmis en 24 heures.
          </p>
          <div className="mt-8 space-y-5">
            <p className="text-[15px] text-muted-foreground font-light leading-[1.9]">
              Derrière DJ Val, il y a Stéphane : plus de 200 mariages animés, une note Google de 5,0 sur plus de 40
              avis, une activité déclarée à la SACEM, une assurance responsabilité civile professionnelle Hiscox et un
              SIRET. Autant de points que les salles municipales, les domaines et les comités d'entreprise d'Avignon
              vérifient systématiquement avant de valider un prestataire.
            </p>
            <p className="text-[15px] text-muted-foreground font-light leading-[1.9]">
              Chercher un DJ à Avignon, c'est surtout chercher quelqu'un qui connaît les contraintes locales : cours
              intérieures du centre historique, voisinage immédiat en intra-muros, horaires de coupure variables d'une
              salle à l'autre, accès parfois compliqués pour décharger le matériel. Ces détails ne se devinent pas la
              veille : ils se préparent en amont, lieu par lieu.
            </p>
            <p className="text-[15px] text-muted-foreground font-light leading-[1.9]">
              Le principe est simple et il ne change pas : un seul événement par week-end. Votre soirée n'est jamais la
              deuxième de la journée, le repérage est fait, la playlist est construite avec vous, et le matériel est
              installé bien avant l'arrivée de vos invités.
            </p>
          </div>
        </div>
      </section>

      {/* PRESTATIONS */}
      <section className="py-24 px-6 max-w-7xl mx-auto">
        <div className="text-center mb-16 sr" data-reveal>
          <p className="eyebrow text-[var(--gold)]">Trois univers, une même exigence</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4">Mes prestations DJ à Avignon</h2>
          <span className="gold-rule mx-auto !w-12 mt-6" />
        </div>
        <div className="grid gap-px bg-[var(--gold-line)] md:grid-cols-3 border border-[var(--gold-line)]">
          {PRESTATIONS.map((p, i) => (
            <Link
              key={p.t}
              to={p.to}
              params={p.params as never}
              className="bg-background p-10 sr block hover:bg-card transition-colors"
              data-reveal
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <p className="font-serif-display italic text-[var(--gold)] text-2xl">{String(i + 1).padStart(2, "0")}</p>
              <h3 className="font-serif-display text-2xl text-foreground mt-4">{p.t}</h3>
              <span className="gold-rule !w-8 my-4" />
              <p className="text-sm text-muted-foreground leading-relaxed font-light">{p.d}</p>
              <span className="mt-5 inline-block text-[11px] uppercase tracking-[0.2em] text-foreground">Voir →</span>
            </Link>
          ))}
        </div>
        <p className="mt-10 text-[15px] text-muted-foreground font-light leading-[1.9] max-w-3xl mx-auto text-center">
          Un mariage à Avignon ne se prépare pas comme une soirée d'entreprise : le premier demande une construction
          minute par minute, de la cérémonie au dernier morceau ; le second exige une image irréprochable, une
          facturation conforme et une capacité à faire danser des collègues qui ne se connaissent pas tous. C'est la
          même personne, avec la même exigence, mais jamais le même déroulé.
        </p>
      </section>

      {/* SONO & LOCATION */}
      <section className="py-24 px-6 border-y border-[var(--gold-line)] bg-card">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-14 items-center">
          <div className="sr-left" data-reveal>
            <img
              src={equipmentImg}
              alt="Régie DJ, sono et éclairage installés pour une soirée à Avignon"
              className="w-full aspect-[4/3] object-cover border border-[var(--gold-line)]"
              loading="lazy"
              decoding="async"
              width={1000}
              height={750}
            />
          </div>
          <div className="sr-right" data-reveal>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground">
              Sono, lumière & location de matériel à Avignon
            </h2>
            <span className="gold-rule !w-12 my-8" />
            <p className="text-[15px] text-muted-foreground font-light leading-[1.9]">
              Le matériel fait partie de la prestation : rien n'est facturé en supplément le jour J. Diffusion
              dimensionnée à la salle, éclairage de piste, micros sans fil pour les discours, machine à fumée si le lieu
              l'autorise, et un second jeu de matériel de secours dans le véhicule.
            </p>
            <p className="mt-5 text-[15px] text-muted-foreground font-light leading-[1.9]">
              Si vous animez vous-même votre soirée, le même matériel se loue seul, avec retrait gratuit à Cavaillon ou
              livraison à Avignon.
            </p>
            <ul className="mt-8 space-y-3 text-sm">
              <li>
                <Link to="/sono-lumiere" className="text-[var(--gold)] underline underline-offset-4">
                  Sonorisation et éclairage pour vos événements
                </Link>
              </li>
              <li>
                <Link to="/location-materiel" className="text-[var(--gold)] underline underline-offset-4">
                  Location de matériel son et lumière dans le Vaucluse
                </Link>
              </li>
              <li>
                <Link to="/location-photobooth-avignon" className="text-[var(--gold)] underline underline-offset-4">
                  Location de photobooth à Avignon
                </Link>
              </li>
              <li>
                <Link to="/soiree-karaoke-avignon" className="text-[var(--gold)] underline underline-offset-4">
                  Soirée karaoké à Avignon animée par un DJ
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* ZONE */}
      <section className="py-24 px-6 max-w-6xl mx-auto">
        <div className="text-center mb-12 sr" data-reveal>
          <p className="eyebrow text-[var(--gold)]">Autour d'Avignon</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4">
            Lieux où j'interviens autour d'Avignon
          </h2>
          <span className="gold-rule mx-auto !w-12 mt-6" />
          <p className="text-sm text-muted-foreground font-light mt-6 max-w-2xl mx-auto">
            {AUTOUR.join(" · ")} — et l'ensemble du Vaucluse, du Luberon et des Alpilles, sans frais de déplacement
            depuis Cavaillon.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {[
            { to: "/lieux" as const, label: "Domaines & salles de réception", desc: "Les lieux du Vaucluse et des Alpilles où j'installe régulièrement." },
            { to: "/dj-vaucluse" as const, label: "DJ dans le Vaucluse", desc: "Toutes mes prestations à l'échelle du département 84." },
            { to: "/realisations" as const, label: "Réalisations récentes", desc: "Des soirées détaillées : lieu, contraintes, déroulé et résultat." },
          ].map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="border border-[var(--gold-line)] bg-card p-8 sr block hover:border-[var(--gold)] transition-colors"
              data-reveal
            >
              <h3 className="font-serif-display text-xl text-foreground">{l.label}</h3>
              <span className="gold-rule !w-8 my-4" />
              <p className="text-sm text-muted-foreground leading-relaxed font-light">{l.desc}</p>
            </Link>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          {AUTOUR.map((v) => (
            <span
              key={v}
              className="px-5 py-2.5 border border-[var(--gold-line)] text-foreground/90 text-xs uppercase tracking-[0.15em]"
            >
              {v}
            </span>
          ))}
        </div>
      </section>

      {/* AVIS */}
      <section className="border-y border-[var(--gold-line)]">
        <div className="pt-16 text-center">
          <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground">Avis clients</h2>
          <span className="gold-rule mx-auto !w-12 mt-6" />
        </div>
        <Suspense fallback={<div className="min-h-[30vh]" />}>
          <GoogleReviewsCarousel />
        </Suspense>
      </section>

      {/* TARIFS */}
      <section className="py-24 px-6 max-w-4xl mx-auto">
        <div className="sr" data-reveal>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground">Tarifs DJ Avignon</h2>
          <span className="gold-rule !w-12 my-8" />
          <p className="text-[15px] text-muted-foreground font-light leading-[1.9]">
            Les fourchettes ci-dessous couvrent la très grande majorité des demandes reçues pour Avignon. Elles
            incluent le déplacement, l'installation, la sonorisation, l'éclairage, les micros et le démontage.
          </p>
          <div className="mt-8 grid gap-px bg-[var(--gold-line)] sm:grid-cols-3 border border-[var(--gold-line)]">
            {[
              { t: "Mariage", p: "dès 1 490 €", d: "Journée complète, de la cérémonie à la fin de soirée." },
              { t: "Anniversaire & soirée privée", p: "690 € à 990 €", d: "Quatre à six heures d'animation." },
              { t: "Soirée d'entreprise", p: "sur devis", d: "Selon la salle, la jauge et les besoins techniques." },
            ].map((f) => (
              <div key={f.t} className="bg-background p-8">
                <h3 className="font-serif-display text-xl text-foreground">{f.t}</h3>
                <p className="font-serif-display italic text-[var(--gold)] text-2xl mt-3">{f.p}</p>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed font-light">{f.d}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm">
            <Link to="/tarifs" className="text-[var(--gold)] underline underline-offset-4">
              Voir le détail des tarifs et des formules DJ
            </Link>
          </p>
          <div className="mt-6">
            <img
              src={receptionImg}
              alt="Réception de mariage mise en lumière par DJ Val près d'Avignon"
              className="w-full aspect-[16/7] object-cover border border-[var(--gold-line)]"
              loading="lazy"
              decoding="async"
              width={1400}
              height={613}
            />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 max-w-4xl mx-auto border-t border-[var(--gold-line)]">
        <div className="text-center mb-12 sr" data-reveal>
          <p className="eyebrow text-[var(--gold)]">Questions fréquentes</p>
          <h2 className="font-serif-display text-4xl text-foreground mt-4">DJ Avignon · ce qu'on me demande</h2>
          <span className="gold-rule mx-auto !w-12 mt-6" />
        </div>
        <div className="space-y-4">
          {FAQ.map((f) => (
            <details key={f.q} className="group border border-[var(--gold-line)] bg-card p-6 sr" data-reveal>
              <summary className="cursor-pointer font-serif-display text-xl text-foreground flex justify-between items-center gap-4">
                {f.q}
                <span className="text-[var(--gold)] group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-4 text-sm text-muted-foreground leading-relaxed font-light">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 text-center border-t border-[var(--gold-line)]">
        <div className="max-w-2xl mx-auto sr" data-reveal>
          <p className="eyebrow text-[var(--gold)]">Votre date à Avignon</p>
          <h2 className="font-serif-display text-4xl text-foreground mt-4">
            Un DJ soirée Avignon <span className="italic text-[var(--gold)]">disponible</span> pour votre date ?
          </h2>
          <span className="gold-rule mx-auto !w-12 my-8" />
          <p className="text-sm text-muted-foreground font-light max-w-xl mx-auto mb-8">
            Dites-moi la date, le lieu et le nombre d'invités : vous recevez un devis détaillé sous 24 heures, sans
            engagement. Vous pouvez aussi appeler directement.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contactez-nous"
              search={{ sujet: "Devis DJ Avignon" }}
              onClick={() => trackEvent("cta_click", { cta_label: "Demander un devis", cta_location: "dj_avignon_cta" })}
              className="inline-block bg-[var(--gold)] text-[var(--ink)] px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all"
            >
              Demander un devis
            </Link>
            <a
              href="tel:0767908667"
              onClick={() => trackEvent("contact_click", { contact_method: "tel", cta_location: "dj_avignon_cta" })}
              className="inline-block border border-[var(--gold-line)] text-foreground px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors"
            >
              07 67 90 86 67
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
